import { differenceInMinutes, differenceInHours, differenceInDays, format, isValid, parse } from 'date-fns';

const EDITORS = [
  "Adobe", "Photoshop", "Lightroom", "Snapseed", "VSCO", "Canva", "CapCut", "PicsArt", "Facetune", "GIMP", "Pixelmator", "Affinity", "Capture One", "Instagram", "Facebook", "WhatsApp", "Telegram", "Google", "iOS", "Android"
];

const parseExifDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const cleanStr = dateStr.trim().replace(/\0/g, '');
  let date = parse(cleanStr, 'yyyy:MM:dd HH:mm:ss', new Date());
  if (!isValid(date)) {
    date = parse(cleanStr, 'yyyy:MM:dd HH:mm', new Date());
  }
  return isValid(date) ? date : null;
};

const applyOffset = (date, offsetStr) => {
  if (!date || !offsetStr) return { date, hasOffset: false };
  const match = offsetStr.match(/([+-])(\d{2}):(\d{2})/);
  if (!match) return { date, hasOffset: false };
  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3], 10);
  const totalOffsetMinutes = (hours * 60 + minutes) * sign;
  const absoluteTime = new Date(date.getTime() - (totalOffsetMinutes * 60 * 1000));
  return { date: absoluteTime, hasOffset: true, originalOffset: totalOffsetMinutes };
};

const detectPlatform = (tags, fileName) => {
  const sw = tags['Software']?.description?.toLowerCase() || '';
  const make = tags['Make']?.description?.toLowerCase() || '';
  const creator = tags['CreatorTool']?.description?.toLowerCase() || '';
  const fName = fileName.toLowerCase();

  if (sw.includes('facebook') || fName.includes('fb_img')) return 'Facebook';
  if (sw.includes('instagram') || sw.includes('instasize')) return 'Instagram';
  if (sw.includes('whatsapp') || fName.includes('wa')) return 'WhatsApp';
  if (sw.includes('adobe') || creator.includes('adobe') || sw.includes('photoshop') || sw.includes('lightroom')) return 'Adobe Suite';
  if (sw.includes('canva')) return 'Canva';
  if (make || tags['Model']?.description) return 'Native Camera';
  return 'Web/Unknown';
};

export const analyzeForensics = (file, tags) => {
  const fileModified = new Date(file.lastModified);
  const rules = [];
  let riskScore = 0;
  let confidenceScore = 80;

  const rawTags = {
    DateTimeOriginal: tags['DateTimeOriginal']?.description,
    CreateDate: tags['CreateDate']?.description || tags['DateTimeDigitized']?.description,
    ModifyDate: tags['ModifyDate']?.description || tags['DateTime']?.description,
    OffsetTimeOriginal: tags['OffsetTimeOriginal']?.description,
    OffsetTime: tags['OffsetTime']?.description,
    Software: tags['Software']?.description,
    Make: tags['Make']?.description,
    Model: tags['Model']?.description,
    GPSDateStamp: tags['GPSDateStamp']?.description,
  };

  const tOriginal = applyOffset(parseExifDate(rawTags.DateTimeOriginal), rawTags.OffsetTimeOriginal);
  const tDigitized = applyOffset(parseExifDate(rawTags.CreateDate), rawTags.OffsetTimeOriginal);
  const tModified = applyOffset(parseExifDate(rawTags.ModifyDate), rawTags.OffsetTime);

  const platform = detectPlatform(tags, file.name);

  if (!tOriginal.date) {
    rules.push({ code: 'F1', msg: 'Missing DateTimeOriginal (common in stripped images)', risk: 18 });
    confidenceScore -= 25;
  }

  if (tOriginal.date) {
    const diffFileVsOrig = differenceInMinutes(fileModified, tOriginal.date);
    if (diffFileVsOrig < -10) {
      rules.push({ code: 'F2', msg: 'File Modified earlier than EXIF Original', risk: 25 });
    }
    const daysDiff = differenceInDays(fileModified, tOriginal.date);
    if (daysDiff > 7) {
      rules.push({ code: 'F3', msg: `File Modified is ${daysDiff} days later than Original`, risk: 15 });
    }
  }

  if (rawTags.Software) {
    const sw = rawTags.Software.toLowerCase();
    const match = EDITORS.find(e => sw.includes(e.toLowerCase()));
    if (match) {
      rules.push({ code: 'E1', msg: `Software tag indicates editor: "${rawTags.Software}"`, risk: 20 });
    }
  }

  riskScore = rules.reduce((acc, r) => acc + r.risk, 0);
  riskScore = Math.min(riskScore, 100);

  const summary = generateSummary({ file, rawTags, tOriginal, tModified, fileModified, riskScore, confidenceScore, rules, platform });

  return {
    timestamps: {
      original: tOriginal.date,
      digitized: tDigitized.date,
      modified: tModified.date,
      fileModified: fileModified,
    },
    platform,
    rawTags,
    rules,
    scores: {
      risk: riskScore,
      veracity: 100 - riskScore,
      confidence: confidenceScore,
      label: getRiskLabel(riskScore)
    },
    summary
  };
};

const getRiskLabel = (score) => {
  if (score < 20) return 'Original';
  if (score < 40) return 'Likely Original';
  if (score < 60) return 'Metadata Modified';
  return 'Edited/Manipulated';
};

const generateSummary = (data) => {
  const lines = [
    `FORENSIC ANALYSIS REPORT`,
    `Platform: ${data.platform}`,
    `Risk Score: ${data.riskScore}/100`,
    `Veracity: ${100 - data.riskScore}%`,
    `----------------------------------------`,
    ...data.rules.map(r => `[${r.code}] ${r.msg}`)
  ];
  return lines.join('\n');
};